import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Brain, Rocket, Target, Lock, Crown } from 'lucide-react';
import ReferralBanner from '../components/ReferralBanner';
import { useAuthStore } from '../stores/authStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { stripeService } from '../services/stripeService';
import { SUBSCRIPTION_PRICES } from '../config/stripe';
import toast from 'react-hot-toast'; 

const plans = [
  {
    id: 'free',
    name: 'Darmowy',
    price: 0,
    features: [
      'Podstawowa analiza predyspozycji',
      'Dostęp do quizu kariery',
      'Podstawowe rekomendacje zawodów',
      'Ograniczony dostęp do analizy'
    ],
    icon: Brain
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 79,
    interval: 'miesięcznie',
    isPopular: true,
    features: [
      'Pełna analiza predyspozycji',
      'Szczegółowy plan rozwoju kariery',
      'Pełne rekomendacje pracy (3 oferty)',
      'Pełna ścieżka kariery (4-5 ścieżek)',
      '5 generacji nowej analizy na miesiąc',
      'Pierwszeństwo do nowych funkcji'
    ],
    icon: Crown
  }
];

export default function PricingPlans() {
  const { user } = useAuthStore();
  const { plan: currentPlan } = useSubscriptionStore();
  const [discountActive, setDiscountActive] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(79);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  // Sprawdź zniżkę przy montowaniu komponentu
  useEffect(() => {
    if (!user) return;
    
    const discountTime = localStorage.getItem(`referralDiscount_${user.id}`);
    if (discountTime) {
      const expiryTime = parseInt(discountTime);
      if (expiryTime > Date.now()) {
        setDiscountActive(true);
        setDiscountedPrice(Math.round(79 * 0.8)); // 20% zniżki
        
        // Aktualizuj czas pozostały
        const updateTimer = () => {
          const now = Date.now();
          const diff = expiryTime - now;
          
          if (diff <= 0) {
            setDiscountActive(false);
            setTimeLeft(null);
            localStorage.removeItem(`referralDiscount_${user.id}`);
            return;
          }

          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeLeft(`${hours}h ${minutes}m`);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 60000); // Co minutę
        return () => clearInterval(interval);
      }
    }
  }, [user]);

  const handlePlanSelect = async (planId: string) => {
    if (planId === 'free') {
      toast('Wybrano plan darmowy.', { icon: '✅' });
      return;
    }
    
    if (!user) {
      toast.error('Musisz być zalogowany, aby wybrać plan Premium.');
      return;
    }

    // TODO: Przed wdrożeniem na produkcję, zamień te ID na produkcyjne ID cen Stripe!
    const REGULAR_PREMIUM_PRICE_ID_TEST = 'price_1RGhNzEdLqZqOO1eOGAq48Qd';
    const DISCOUNTED_PREMIUM_PRICE_ID_TEST = 'price_1RGhPVEdLqZqOO1eunyQwmtW';

    // Wybierz odpowiednie ID ceny testowej na podstawie aktywnej zniżki
    const priceIdToUse = discountActive
      ? DISCOUNTED_PREMIUM_PRICE_ID_TEST
      : REGULAR_PREMIUM_PRICE_ID_TEST;

    const toastId = toast.loading('Przekierowywanie do płatności...');

    try {
      // Wywołaj funkcję backendową (przez stripeService), aby utworzyć sesję checkout
      const { url: checkoutUrl, sessionId } = await stripeService.createCheckoutSession(
        user.id,
        priceIdToUse
        // Możesz dodać discountPercentage jeśli chcesz go śledzić, np.:
        // discountActive ? 20 : 0
      );

      if (!checkoutUrl) {
        throw new Error('Nie otrzymano URL do strony płatności.');
      }

      toast.success('Zaraz zostaniesz przekierowany...', { id: toastId });
      // Poczekaj chwilę, aby użytkownik zobaczył toasta
      await new Promise(resolve => setTimeout(resolve, 500));

      // Przekieruj użytkownika do strony płatności Stripe
      window.location.href = checkoutUrl; // Użyj location.href dla pełnego przekierowania

    } catch (error: any) {
      console.error('Błąd podczas tworzenia sesji checkout Stripe:', error);
      toast.error(`Nie udało się rozpocząć płatności: ${error.message || 'Nieznany błąd'}`, { id: toastId });
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_right,_rgba(124,58,237,0.15),_transparent_35%),_radial-gradient(circle_at_top_left,_rgba(99,102,241,0.15),_transparent_45%),_radial-gradient(circle_at_bottom_left,_rgba(124,58,237,0.2),_transparent_25%)] py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {currentPlan === 'premium' && (
          <div className="mb-8 bg-purple-50 rounded-xl p-6 text-center">
            <Crown className="w-8 h-8 text-purple-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-purple-900 mb-2">
              Twój aktualny plan: Premium
            </h2>
            <p className="text-purple-700">
              Masz dostęp do wszystkich funkcji premium. Możesz zarządzać swoją subskrypcją w ustawieniach profilu.
            </p>
          </div>
        )}

        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6"
          >
            <Rocket className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">
              Odblokuj pełen potencjał swojej kariery
            </span>
          </motion.div>
          
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Wybierz plan dla siebie
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Zainwestuj w swoją przyszłość i zyskaj dostęp do zaawansowanych narzędzi rozwoju kariery.
            <br />
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const PlanIcon = plan.icon;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden ${
                  plan.isPopular ? 'ring-4 ring-purple-500 ring-opacity-50' : ''
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-2 rounded-bl-xl">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      <span className="text-sm font-medium">Popularny wybór</span>
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      plan.isPopular ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                      <PlanIcon className={`w-6 h-6 ${
                        plan.isPopular ? 'text-purple-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {plan.name}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        {plan.id === 'premium' && discountActive ? (
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold text-gray-900">
                              {discountedPrice}
                            </span>
                            <span className="text-lg line-through text-gray-400">
                              {plan.price}
                            </span>
                            <div className="flex flex-col items-start">
                              <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                -20%
                              </span>
                              {timeLeft && (
                                <span className="text-xs text-gray-500 mt-1">
                                  Zostało: {timeLeft}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-3xl font-bold text-gray-900">
                            {plan.price}
                          </span>
                        )}
                        <span className="text-gray-600">PLN</span>
                        {plan.interval && (
                          <span className="text-sm text-gray-500">
                            /{plan.interval}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 ${
                          plan.isPopular ? 'bg-purple-100' : 'bg-gray-100'
                        } text-purple-600`} />
                        <span className="text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={!user || (plan.id === 'premium' && currentPlan === 'premium')}
                    className={`w-full py-4 rounded-xl font-medium text-center transition-all transform hover:scale-105 ${
                      plan.isPopular
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } ${plan.id === 'premium' && currentPlan === 'premium' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {plan.id === 'premium' && user ? (
                      currentPlan === 'premium' ? (
                        'Aktualny plan'
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <Target className="w-5 h-5" />
                          {discountActive ? (
                            <div className="flex items-center gap-2">
                              <span>Przejdź na Premium</span>
                              <div className="flex flex-col items-start">
                                <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                  -20%
                                </span>
                                {timeLeft && (
                                  <span className="text-xs text-white/80">
                                    ({timeLeft})
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            'Przejdź na Premium'
                          )}
                        </div>
                      )
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Lock className="w-5 h-5" />
                        {plan.id === 'premium' ? 'Zaloguj się aby kontynuować' : 'Wybierz plan darmowy'}
                      </div>
                    )}
                  </button>
                </div>

                {plan.isPopular && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 text-center">
                    <p className="text-sm text-purple-700">
                      Dołącz do tysięcy wybranych, którzy już rozwinęli swoją karierę
                      <br />
                      <span className="text-xs text-gray-600 mt-1">
                        Możesz anulować subskrypcję w dowolnym momencie
                      </span>
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="max-w-4xl mx-auto mt-12">
          <ReferralBanner />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500">
            Masz pytania? Skontaktuj się z nami: <a href="mailto:polokontaktowy@gmail.com" className="text-purple-600 hover:text-purple-700">polokontaktowy@gmail.com</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
